import React, { useState } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Filter, 
  Search, 
  MoreHorizontal, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  FileText, 
  User, 
  MapPin, 
  Calendar, 
  ShieldCheck, 
  History,
  Download,
  ExternalLink,
  ChevronRight,
  Fingerprint,
  Zap,
  Info,
  ShieldAlert,
  FileCheck,
  Eye,
  Scan,
  Database
} from 'lucide-react';
import { EntityKYC } from '../../types';

interface KYCViewProps {
  onBack: () => void;
}

const MOCK_KYC: EntityKYC[] = [
  { id: 'KYC-1001', name: 'Alice Thompson', type: 'Individual', status: 'Verified', date: '2023-10-24', risk: 'Low' },
  { id: 'KYC-1002', name: 'Bob Richards', type: 'Individual', status: 'Pending', date: '2023-10-25', risk: 'Medium' },
  { id: 'KYC-1003', name: 'Charlie Dean', type: 'Individual', status: 'Rejected', date: '2023-10-26', risk: 'High' },
  { id: 'KYC-1004', name: 'David Miller', type: 'Individual', status: 'Verified', date: '2023-10-22', risk: 'Low' },
];

export const KYCView: React.FC<KYCViewProps> = ({ onBack }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedKYC, setSelectedKYC] = useState<EntityKYC | null>(null);

  return (
    <div className="p-6 space-y-6 animate-in slide-in-from-right duration-500 relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Individual e-KYC</h1>
            <p className="text-sm text-slate-500">AI-powered identity verification & biometric matching.</p>
          </div>
        </div>
        <button 
          onClick={() => setIsFormOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2 shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95"
        >
          <Plus size={18} />
          <span>New Verification</span>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 w-80 shadow-inner">
            <Search size={14} className="text-slate-400" />
            <input type="text" placeholder="Search by name or ID..." className="text-sm outline-none w-full font-medium" />
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 transition-all">
              <Filter size={18} />
            </button>
          </div>
        </div>

        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500 border-b border-slate-100">
            <tr>
              <th className="px-6 py-4">Client Identity</th>
              <th className="px-6 py-4">Verification ID</th>
              <th className="px-6 py-4">Risk Rating</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {MOCK_KYC.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => setSelectedKYC(item)}>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm uppercase shadow-sm">
                      {item.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{item.name}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item.date}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-xs font-mono text-slate-500 font-medium">{item.id}</td>
                <td className="px-6 py-4">
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border tracking-widest ${
                    item.risk === 'High' ? 'bg-rose-50 text-rose-600 border-rose-100' : 
                    item.risk === 'Medium' ? 'bg-amber-50 text-amber-600 border-amber-100' : 
                    'bg-emerald-50 text-emerald-600 border-emerald-100'
                  }`}>
                    {item.risk} Risk
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    {item.status === 'Verified' && <CheckCircle2 size={16} className="text-emerald-500" />}
                    {item.status === 'Pending' && <Clock size={16} className="text-amber-500" />}
                    {item.status === 'Rejected' && <XCircle size={16} className="text-rose-500" />}
                    <span className="text-sm font-bold text-slate-700">{item.status}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={(e) => { e.stopPropagation(); setSelectedKYC(item); }}
                    className="p-2 text-slate-400 hover:text-slate-900 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 rounded-lg transition-all"
                    title="View Detailed Dossier"
                  >
                    <MoreHorizontal size={20} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detailed Verification Modal */}
      {selectedKYC && (
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setSelectedKYC(null)}></div>
          <div className="relative bg-white w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-[2.5rem] shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 border border-slate-200">
            {/* Modal Header */}
            <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/30">
              <div className="flex items-center space-x-6">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-[1.5rem] flex items-center justify-center shadow-xl shadow-blue-200">
                   <ShieldCheck size={32} />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">Identity Dossier: {selectedKYC.name}</h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">{selectedKYC.id}</span>
                    <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                    <span className="text-xs text-slate-400 font-medium">Verified via Crystal-AI Engine</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setSelectedKYC(null)} className="p-3 hover:bg-white hover:shadow-sm rounded-2xl text-slate-400 hover:text-slate-900 transition-all border border-transparent hover:border-slate-100">
                <XCircle size={28} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
              {/* Profile Card & Biographic Info */}
              <div className="lg:col-span-4 space-y-8">
                <div className="text-center p-8 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden group">
                  <div className="relative z-10">
                    <div className="relative inline-block mb-6">
                      <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedKYC.name}`} alt="Avatar" className="w-32 h-32 mx-auto bg-white rounded-3xl shadow-xl border-4 border-white" />
                      <div className={`absolute -bottom-2 -right-2 p-2 rounded-2xl shadow-lg ${selectedKYC.status === 'Verified' ? 'bg-emerald-500' : 'bg-amber-500'} text-white`}>
                         {selectedKYC.status === 'Verified' ? <CheckCircle2 size={18} /> : <Clock size={18} />}
                      </div>
                    </div>
                    <h3 className="text-xl font-black text-slate-900">{selectedKYC.name}</h3>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6">{selectedKYC.type} Holder</p>
                    
                    <div className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-sm inline-block ${
                      selectedKYC.risk === 'High' ? 'bg-rose-100 text-rose-700 border border-rose-200' : 
                      selectedKYC.risk === 'Medium' ? 'bg-amber-100 text-amber-700 border border-amber-200' : 
                      'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    }`}>
                      {selectedKYC.risk} Risk Profile
                    </div>
                  </div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2 group-hover:opacity-40 transition-opacity"></div>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Biographic Intelligence</h4>
                    <Database size={12} className="text-blue-500" />
                  </div>
                  <div className="space-y-4">
                    <InfoItem icon={<User size={16} />} label="Nationality" value="United Kingdom" />
                    <InfoItem icon={<Calendar size={16} />} label="Date of Birth" value="14 May 1985" />
                    <InfoItem icon={<MapPin size={16} />} label="Address" value="SW1A 1AA, London" />
                    <InfoItem icon={<FileCheck size={16} />} label="Residency" value="Verified Permanent" />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Document Scans</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="aspect-[3/2] bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200 group relative overflow-hidden cursor-pointer">
                      <FileText className="text-slate-400 group-hover:scale-110 transition-transform" />
                      <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-[10px] text-white font-bold uppercase tracking-widest">Passport Front</span>
                      </div>
                    </div>
                    <div className="aspect-[3/2] bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200 group relative overflow-hidden cursor-pointer">
                      <Scan className="text-slate-400 group-hover:scale-110 transition-transform" />
                      <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-[10px] text-white font-bold uppercase tracking-widest">Biometric Photo</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Analysis & Decision Logic */}
              <div className="lg:col-span-8 space-y-8">
                <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl relative overflow-hidden group">
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-8">
                       <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest flex items-center">
                         <Zap size={16} className="mr-2" /> Neural Engine Validation Results
                       </h4>
                       <div className="flex items-center space-x-2 bg-white/10 px-3 py-1 rounded-full border border-white/10">
                         <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                         <span className="text-[9px] font-black uppercase tracking-widest">Confidence Score: 98.4%</span>
                       </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8">
                      <ValidationMeter title="Document Authenticity" score={99} detail="Anti-tamper checks passed" />
                      <ValidationMeter title="Biometric Matching" score={selectedKYC.status === 'Verified' ? 94 : 0} detail="Face-to-ID Liveness sync" />
                      <ValidationMeter title="Watchlist Screening" score={selectedKYC.risk === 'High' ? 12 : 100} detail="Sanctions & PEP matching" />
                      <ValidationMeter title="OCR Confidence" score={97} detail="Data extraction accuracy" />
                    </div>
                  </div>
                  {/* Decorative background gradients */}
                  <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-[120px] opacity-10 -translate-y-1/2 translate-x-1/2"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col justify-between">
                    <div>
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Screening Intelligence</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center text-xs font-bold text-slate-700">
                          <span>UN Sanctions List</span>
                          <span className="text-emerald-600">No Match</span>
                        </div>
                        <div className="flex justify-between items-center text-xs font-bold text-slate-700">
                          <span>EU Consolidated</span>
                          <span className="text-emerald-600">No Match</span>
                        </div>
                        <div className="flex justify-between items-center text-xs font-bold text-slate-700">
                          <span>OFAC SDN</span>
                          <span className="text-emerald-600">No Match</span>
                        </div>
                      </div>
                    </div>
                    <button className="mt-6 w-full py-2 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-blue-600 hover:bg-white hover:shadow-sm transition-all">
                      View Raw JSON Report
                    </button>
                  </div>

                  <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Compliance Audit Log</h4>
                    <div className="space-y-6 relative before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-50">
                       <AuditTrailItem icon={<Clock size={10} />} title="Case Initiated" date="14:02:11" />
                       <AuditTrailItem icon={<FileText size={10} />} title="Document Uploaded" date="14:03:45" />
                       <AuditTrailItem icon={<Zap size={10} />} title="AI Scoring Completed" date="14:05:02" />
                       <AuditTrailItem icon={<User size={10} />} title="Officer Final Review" date="15:12:10" active />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="p-8 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <div className="flex space-x-3">
                <button className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-blue-600 transition-all hover:shadow-md">
                   <Download size={20} />
                </button>
                <button className="p-3 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-blue-600 transition-all hover:shadow-md">
                   <ExternalLink size={20} />
                </button>
              </div>
              <div className="flex space-x-4">
                <button 
                  onClick={() => setSelectedKYC(null)}
                  className="px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-100 transition-all shadow-sm"
                >
                  Close Dossier
                </button>
                {selectedKYC.status !== 'Verified' && (
                  <button className="px-8 py-3 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-500/20 transition-all active:scale-95 flex items-center space-x-2">
                    <ShieldCheck size={18} />
                    <span>Approve Entity</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Slide-over Verification Form */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsFormOpen(false)}></div>
          <div className="absolute inset-y-0 right-0 max-w-xl w-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 rounded-l-[3rem] border-l border-slate-100">
            <div className="p-10 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-tl-[3rem]">
              <div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">New KYC Session</h2>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Initiate manual onboarding flow</p>
              </div>
              <button onClick={() => setIsFormOpen(false)} className="p-3 hover:bg-white rounded-2xl text-slate-400 hover:text-slate-900 shadow-sm transition-all border border-transparent hover:border-slate-100">
                <XCircle size={24} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-10 space-y-10">
              <div className="space-y-6">
                <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center">
                  <User size={14} className="mr-2" /> Personal Attributes
                </h3>
                <div className="grid grid-cols-2 gap-6">
                   <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">First Name</label>
                     <input type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" placeholder="John" />
                   </div>
                   <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Last Name</label>
                     <input type="text" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all" placeholder="Doe" />
                   </div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest flex items-center">
                  <FileText size={14} className="mr-2" /> Primary Documentation
                </h3>
                <div className="border-2 border-dashed border-slate-200 rounded-[2rem] p-12 flex flex-col items-center justify-center space-y-4 hover:border-blue-400 hover:bg-blue-50/50 transition-all cursor-pointer group">
                  <div className="p-4 bg-blue-100 text-blue-600 rounded-[1.25rem] group-hover:scale-110 transition-transform shadow-sm"><FileText size={28} /></div>
                  <div className="text-center">
                    <p className="text-sm font-black text-slate-900">Drag & drop identity files</p>
                    <p className="text-[10px] text-slate-400 font-bold uppercase mt-1 tracking-widest">Passport, License, or ID (Max 25MB)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-10 border-t border-slate-100 bg-slate-50 flex space-x-4 rounded-bl-[3rem]">
              <button onClick={() => setIsFormOpen(false)} className="flex-1 py-4 bg-white border border-slate-200 rounded-2xl text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all active:scale-95 shadow-sm">
                Cancel
              </button>
              <button className="flex-1 py-4 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 shadow-xl shadow-slate-200 transition-all active:scale-95">
                Launch Verification
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem: React.FC<{ icon: React.ReactNode, label: string, value: string }> = ({ icon, label, value }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center space-x-3 text-slate-400">
      <div className="p-1.5 bg-slate-100 rounded-lg">{icon}</div>
      <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
    </div>
    <span className="text-xs font-black text-slate-800">{value}</span>
  </div>
);

const ValidationMeter: React.FC<{ title: string, score: number, detail: string }> = ({ title, score, detail }) => (
  <div className="space-y-3">
    <div className="flex justify-between items-end">
       <div>
         <p className="text-sm font-bold text-white mb-0.5">{title}</p>
         <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest leading-tight">{detail}</p>
       </div>
       <span className={`text-xs font-black ${score >= 90 ? 'text-emerald-400' : score > 50 ? 'text-amber-400' : 'text-rose-400'}`}>
         {score === 0 ? '--' : `${score}%`}
       </span>
    </div>
    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
      <div 
        className={`h-full transition-all duration-1000 ease-out ${score >= 90 ? 'bg-emerald-400' : score > 50 ? 'bg-amber-400' : 'bg-rose-400'}`} 
        style={{ width: `${score}%` }}
      ></div>
    </div>
  </div>
);

const AuditTrailItem: React.FC<{ icon: React.ReactNode, title: string, date: string, active?: boolean }> = ({ icon, title, date, active }) => (
  <div className="flex items-center justify-between relative pl-8 group">
    <div className={`absolute left-0 w-4 h-4 rounded-full border-2 border-white z-10 flex items-center justify-center transition-all ${active ? 'bg-blue-600 scale-125 shadow-lg shadow-blue-500/40' : 'bg-slate-200'}`}>
       <div className="text-white scale-75">{icon}</div>
    </div>
    <span className={`text-[11px] font-black uppercase tracking-tight transition-all ${active ? 'text-blue-600' : 'text-slate-500'}`}>{title}</span>
    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{date}</span>
  </div>
);
